import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import axios from 'axios';

import { getApiErrorMessage } from '../utils/apiError';
import PageShell from '../components/PageShell';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const PAGE_SIZE = 50;

interface OperationLogItem {
  id: number;
  target_id: number | null;
  status: string;
  error?: string | null;
  created_at?: string | null;
}

interface OperationLog {
  id: number;
  user_id: number;
  operation_type: string;
  target_domain: string;
  requested_changes?: Record<string, unknown> | null;
  total: number;
  succeeded: number;
  failed: number;
  created_at?: string | null;
}

interface OperationLogDetail extends OperationLog {
  items: OperationLogItem[];
}

const OPERATION_TYPE_LABELS: Record<string, string> = {
  habit_bulk_edit: 'Bulk habit edit',
};

const humanizeOperationType = (type: string): string => {
  if (OPERATION_TYPE_LABELS[type]) return OPERATION_TYPE_LABELS[type];
  const spaced = type.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const FIELD_LABELS: Record<string, string> = {
  is_active: 'Active',
  check_in_enabled: 'Check-in enabled',
  check_in_priority: 'Check-in priority',
  recurrence_type: 'Recurrence type',
  recurrence_interval: 'Recurrence interval',
  target_count: 'Target count',
};

const humanizeField = (key: string): string => FIELD_LABELS[key] ?? key;

const formatValue = (value: unknown): string => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (value === null || value === undefined) return '—';
  return String(value);
};

const summarizeChanges = (
  changes: Record<string, unknown> | null | undefined,
): string => {
  if (!changes || Object.keys(changes).length === 0) return '—';
  return Object.entries(changes)
    .map(([key, value]) => `${humanizeField(key)} → ${formatValue(value)}`)
    .join(', ');
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

// A single target label. Only the "habits" domain produces logs today, so a
// bare id is rendered as "Habit #12"; any future domain falls back to "#12".
const targetLabel = (domain: string, targetId: number | null): string => {
  if (targetId == null) return '—';
  if (domain === 'habits') return `Habit #${targetId}`;
  return `#${targetId}`;
};

const Activity: React.FC = () => {
  const {
    cardContentPaddingSx,
    dialogContentPaddingSx,
    pageHeaderSx,
    tableCellPaddingSx,
  } = useLayoutTokens();
  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [detail, setDetail] = useState<OperationLogDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  // Monotonic token so a slow, earlier detail fetch can't overwrite the
  // operation the user currently has open (or one they've since closed).
  const detailReqRef = useRef(0);

  const fetchPage = async (offset: number): Promise<OperationLog[]> => {
    const response = await axios.get<OperationLog[]>(
      `${API_BASE_URL}/api/operation-logs`,
      { params: { limit: PAGE_SIZE, offset } },
    );
    return response.data;
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const rows = await fetchPage(0);
        setLogs(rows);
        setHasMore(rows.length === PAGE_SIZE);
        setError(null);
      } catch (err: any) {
        setError(getApiErrorMessage(err, 'Failed to load activity log'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleLoadMore = async () => {
    try {
      setLoadingMore(true);
      const rows = await fetchPage(logs.length);
      setLogs((prev) => [...prev, ...rows]);
      setHasMore(rows.length === PAGE_SIZE);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load more activity'));
    } finally {
      setLoadingMore(false);
    }
  };

  const openDetail = async (operationId: number) => {
    const token = ++detailReqRef.current;
    setDetailOpen(true);
    setDetail(null);
    setDetailError(null);
    try {
      setDetailLoading(true);
      const response = await axios.get<OperationLogDetail>(
        `${API_BASE_URL}/api/operation-logs/${operationId}`,
      );
      if (detailReqRef.current === token) setDetail(response.data);
    } catch (err: any) {
      if (detailReqRef.current === token) {
        setDetailError(getApiErrorMessage(err, 'Failed to load operation details'));
      }
    } finally {
      if (detailReqRef.current === token) setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    // Invalidate any in-flight detail fetch so its late resolution is ignored.
    detailReqRef.current += 1;
    setDetailOpen(false);
  };

  if (loading) {
    return (
      <PageShell maxWidth="standard">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="standard">
      <Box sx={pageHeaderSx}>
        <Typography variant="h3" component="h1">
          Activity
        </Typography>
        <Typography variant="body1" color="text.secondary">
          A record of bulk and administrative operations, with the outcome of each item.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {logs.length === 0 ? (
        // Only claim "nothing here" when the list actually loaded empty — not
        // when it failed (the error Alert above already explains that case).
        !error && (
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <Typography variant="h6" gutterBottom>
                No activity yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bulk operations — such as editing several habits at once from the Habits page —
                will appear here so you can review what changed and whether anything failed.
              </Typography>
            </CardContent>
          </Card>
        )
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small" sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}>
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Operation</TableCell>
                  <TableCell>Changes</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell align="right">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>{formatDateTime(log.created_at)}</TableCell>
                    <TableCell>{humanizeOperationType(log.operation_type)}</TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
                        {summarizeChanges(log.requested_changes)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label={`${log.succeeded} ok`}
                        />
                        {log.failed > 0 && (
                          <Chip
                            size="small"
                            color="error"
                            variant="outlined"
                            label={`${log.failed} failed`}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => openDetail(log.id)}
                        aria-label={`View details for operation ${log.id}`}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {hasMore && (
            <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
              <Button onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? <CircularProgress size={20} /> : 'Load more'}
              </Button>
            </Box>
          )}
        </>
      )}

      <Dialog open={detailOpen} onClose={closeDetail} fullWidth maxWidth="sm">
        <DialogTitle>Operation Details</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={160}>
              <CircularProgress />
            </Box>
          ) : detailError ? (
            <Alert severity="error">{detailError}</Alert>
          ) : detail ? (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {humanizeOperationType(detail.operation_type)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {formatDateTime(detail.created_at)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Changes:</strong> {summarizeChanges(detail.requested_changes)}
              </Typography>
              <Alert
                severity={detail.failed > 0 ? 'warning' : 'success'}
                sx={{ mb: 2 }}
              >
                {detail.succeeded} of {detail.total} succeeded
                {detail.failed > 0 ? `, ${detail.failed} failed` : ''}.
              </Alert>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small" sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Target</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{targetLabel(detail.target_domain, item.target_id)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={item.status === 'ok' ? 'success' : 'error'}
                            variant="outlined"
                            label={item.status === 'ok' ? 'OK' : 'Failed'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={item.error ? 'error' : 'text.secondary'}>
                            {item.error || '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default Activity;
